<template>
  <v-dialog
    v-model="dialog"
    max-width="560"
    overlay-opacity="0.8"
    :persistent="loading"
  >
    <v-card class="rounded-xl">
      <v-card-title class="text-body-1 font-weight-bold">
        Buscar cliente
      </v-card-title>
      <v-card-text>
        <v-btn-toggle
          v-model="type"
          mandatory
          dense
          color="primary"
          class="d-flex mb-4"
        >
          <v-btn
            v-for="option in types"
            :key="option.value"
            :value="option.value"
            class="flex-grow-1"
            small
            :disabled="loading"
          >
            {{ option.label }}
          </v-btn>
        </v-btn-toggle>

        <v-text-field
          v-if="type === 'name'"
          v-model="query"
          label="Nome"
          placeholder="Nome do cliente"
          outlined
          dense
          hide-details
          autofocus
          autocomplete="off"
          @input="scheduleNameSearch"
          @keyup.enter="search"
        />

        <v-text-field-simplemask
          v-else-if="type === 'document'"
          v-model="query"
          label="CPF"
          :properties="{
            outlined: true,
            dense: true,
            hideDetails: true,
            placeholder: '###.###.###-##',
            type: 'tel',
            autocomplete: 'off',
            disabled: loading,
            autofocus: true,
          }"
          :options="{
            inputMask: '###.###.###-##',
            outputMask: '###.###.###-##',
            applyAfter: false,
            alphanumeric: false,
            lowerCase: false,
          }"
          @keyup.enter.native="search"
        />

        <phone-input
          v-else-if="type === 'phone'"
          :phone="phone"
          :ddi="ddi"
          label="Telefone"
          dense
          hide-details
          :disabled="loading"
          @update:phone="(value) => (phone = value)"
          @update:ddi="(value) => (ddi = value)"
          @valid="(value) => (phoneValid = value)"
        />

        <v-text-field
          v-else
          v-model="query"
          label="E-mail"
          placeholder="email@exemplo.com"
          outlined
          dense
          hide-details
          type="email"
          inputmode="email"
          autocomplete="off"
          autocapitalize="none"
          spellcheck="false"
          :disabled="loading"
          @blur="formatEmail"
          @keyup.enter="search"
        />

        <p v-if="type === 'name'" class="text-caption mb-0 mt-2">
          A busca mostra os nomes mais parecidos.
        </p>

        <v-alert v-if="error" type="error" dense text class="mt-3 mb-0">
          {{ error }}
        </v-alert>
        <v-alert
          v-else-if="notFound"
          type="info"
          dense
          text
          class="mt-3 mb-0"
        >
          Nenhum cliente encontrado.
        </v-alert>

        <v-list
          v-if="type === 'name' && results.length"
          two-line
          class="mt-2"
          style="max-height: 320px; overflow-y: auto"
        >
          <v-list-item
            v-for="user in results"
            :key="user.id"
            @click="openDetails(user)"
          >
            <v-list-item-avatar>
              <v-avatar v-if="!user.name" size="40">
                <v-icon>mdi-account-question</v-icon>
              </v-avatar>
              <base-avatar
                v-else
                :src="user.photo"
                :seed="user.id"
                :size="40"
              />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>
                {{ user.name || "Conta não criada" }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ user.phone }}
                <template v-if="user.email"> • {{ user.email }}</template>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions>
        <v-btn text :disabled="loading" @click="close">Fechar</v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!canSearch"
          @click="search"
        >
          Buscar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import USERS from "@/services/ultimate/users";
import PhoneInput from "@/components/global/PhoneInput.vue";
import validateCpf from "@/utils/validate-cpf";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
  name: "UserSearchModal",
  components: { PhoneInput },
  data: () => ({
    dialog: false,
    type: "name",
    query: "",
    phone: "",
    ddi: 55,
    phoneValid: false,
    results: [],
    loading: false,
    error: null,
    notFound: false,
    requestId: 0,
    nameTimer: null,
    types: [
      { value: "name", label: "Nome" },
      { value: "document", label: "CPF" },
      { value: "phone", label: "Telefone" },
      { value: "email", label: "E-mail" },
    ],
  }),
  computed: {
    isQueryReady() {
      if (this.type === "name") return this.query.trim().length >= 2;
      if (this.type === "document") return validateCpf(this.query || "");
      if (this.type === "phone") return this.phoneValid;
      return EMAIL_PATTERN.test(this.query.trim());
    },
    canSearch() {
      return this.isQueryReady && !this.loading;
    },
  },
  watch: {
    type() {
      this.query = "";
      this.phone = "";
      this.ddi = 55;
      this.phoneValid = false;
      this.results = [];
      this.error = null;
      this.notFound = false;
      clearTimeout(this.nameTimer);
    },
  },
  methods: {
    open() {
      this.type = "name";
      this.query = "";
      this.phone = "";
      this.ddi = 55;
      this.phoneValid = false;
      this.results = [];
      this.error = null;
      this.notFound = false;
      this.loading = false;
      this.dialog = true;
    },
    close() {
      clearTimeout(this.nameTimer);
      this.dialog = false;
    },
    formatEmail() {
      this.query = this.query.trim().toLowerCase();
    },
    scheduleNameSearch() {
      clearTimeout(this.nameTimer);
      this.error = null;
      this.notFound = false;
      if (this.query.trim().length < 2) {
        this.results = [];
        return;
      }
      this.nameTimer = setTimeout(() => this.search(), 350);
    },
    async search() {
      if (!this.isQueryReady) return;
      if (this.type === "email") this.formatEmail();

      const requestId = ++this.requestId;
      this.loading = true;
      this.error = null;
      this.notFound = false;

      const params = { type: this.type };
      if (this.type === "phone") {
        params.q = this.phone;
        params.ddi = this.ddi;
      } else {
        params.q = this.query.trim();
      }

      try {
        const response = await USERS.searchUsers(params);
        if (requestId !== this.requestId) return;

        const users = response.users || [];
        if (this.type === "name") {
          this.results = users;
          this.notFound = users.length === 0;
          return;
        }

        if (!users.length) {
          this.notFound = true;
          return;
        }

        this.openDetails(users[0]);
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.results = [];
        this.error = error.message || "Erro ao buscar cliente";
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    openDetails(user) {
      const exact = this.type !== "name";
      if (exact) this.close();
      setTimeout(() => {
        this.$root.$emit("user-details-modal", user);
      }, exact ? 280 : 0);
    },
  },
  beforeDestroy() {
    clearTimeout(this.nameTimer);
  },
};
</script>
